/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { ColumnsType } from 'antd/es/table';

import { User } from '@/client';
import { RelativeTime } from '@/components/RelativeTime';
import { canPerformAction } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { UserActionDropdown } from '@/pages/Users/UserActionDropdown';
import { Role } from '@/pages/Users/UserTable';
import { Avatar } from '@/primitives/Avatar';
import { Flex } from '@/primitives/Flex';
import { Space } from '@/primitives/Space';
import { Text } from '@/primitives/Text';
import { Tooltip } from '@/primitives/Tooltip';

export function useActiveUsersColumns(
  currentUser: User,
  onChangeRemoved: (user: User, removed: boolean) => void,
  onChangeUpdated: (user: User, role: Role) => void,
  openUserSubtenantsModal: (user: User) => void,
  navigateToUserDetails: (user: User) => void,
  toggleSubtenantOnly: (user: User) => void,
): ColumnsType<User> {
  const { t } = useMimicTranslation('users');

  const activeUsersColumns: ColumnsType<User> = [
    {
      title: t('name'),
      dataIndex: 'displayName',
      key: 'displayName',
      render: (_text, record) => {
        const [firstName, lastName] = record.displayName.split(' ');
        return (
          <Flex>
            <Avatar text={`${firstName[0]}${lastName[0]}`} size="sm" />
            <Flex vertical gap="none">
              <Text size="lg">{record.displayName}</Text>
              <Text style={{ color: 'rgba(0, 0, 0, 0.56)' }}>{record.email}</Text>
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: t('columns.subtenantOnly'),
      dataIndex: 'subtenantOnly',
      key: 'subtenantOnly',
      width: 140,
      render: (subtenantOnly: User['subtenantOnly']) => {
        return (
          <Tooltip title={t('columns.subtenantOnlyTooltip')}>
            <div style={{ textAlign: 'center' }}>{subtenantOnly ? '✓' : '✗'}</div>
          </Tooltip>
        );
      },
    },
    {
      title: t('columns.role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: User['role']) => t(`roles.${role}`),
    },
    {
      title: t('columns.dateCreated'),
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      render: (dateCreated: User['dateCreated']) => (
        <Space minHeight="40px">
          <RelativeTime date={dateCreated} />
        </Space>
      ),
    },
  ];

  if (!canPerformAction('admin', currentUser)) return activeUsersColumns;

  return [
    ...activeUsersColumns,
    {
      title: '',
      dataIndex: '',
      width: '100px',
      key: 'actions',
      render: (_, record) => {
        return (
          <UserActionDropdown
            user={record}
            currentUser={currentUser}
            openUserSubtenantsModal={openUserSubtenantsModal}
            onChangeRemoved={onChangeRemoved}
            onChangeUpdated={onChangeUpdated}
            navigateToUserDetails={navigateToUserDetails}
            toggleSubtenantOnly={toggleSubtenantOnly}
          />
        );
      },
    },
  ];
}
